import I18n from 'I18n';
import { assign } from 'fsm';
import Raven from 'raven-js';
import { runSmartFill, getEmptyPropertiesCount, getEmptyPropertiesCountByIds } from './client';
import { fetchSmartProperties } from '../../client';
import { getObjectType, wait } from '../../../utils';
import { usageTracker } from '../../../../usageTracker';
import { getEnrichmentTypeFromObjectTypeId } from '../../../../utils/getEnrichmentTypeFromEnrichmentId';
export const machine = {
  initial: 'initializing',
  states: {
    initializing: {
      always: [{
        target: 'loadingUpdates',
        actions: ['setEnrichmentType', 'trackOpenPanel']
      }]
    },
    loadingUpdates: {
      invoke: {
        src: 'fetchSmartProperties',
        onDone: [{
          target: 'loadingEmptyProperties',
          actions: ['setSmartProperties'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'idle',
          actions: ['setSmartProperties']
        }, {
          target: 'idle',
          actions: ['setEmptyProperties']
        }],
        onError: [{
          target: 'error',
          actions: ['setErrorId', 'logError']
        }]
      },
      tags: ['loading']
    },
    loadingEmptyProperties: {
      invoke: {
        src: 'getEmptyPropertiesCount',
        onDone: {
          target: 'idle',
          actions: ['setEmptyPropertiesCount']
        },
        onError: {
          target: 'idle',
          actions: ['logError']
        }
      },
      tags: ['loading']
    },
    loadingEmptyPropertiesByIds: {
      invoke: {
        src: 'getEmptyPropertiesCountByIds',
        onDone: {
          target: 'idle',
          actions: ['setEmptyPropertiesCount']
        },
        onError: {
          target: 'idle',
          actions: ['logError']
        }
      },
      tags: ['loading']
    },
    refreshingEmptyProperties: {
      invoke: {
        src: 'getEmptyPropertiesCount',
        onDone: {
          target: 'idle',
          actions: ['setEmptyPropertiesCount']
        },
        onError: {
          target: 'idle',
          actions: ['logError']
        }
      },
      on: {
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        SELECT_PROPERTIES: [{
          target: 'refreshingEmptyProperties',
          actions: ['setSelectedProperties'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setSelectedProperties'],
          cond: 'isIdSelectionMethodWithProperties'
        }, {
          target: 'idle',
          actions: ['setSelectedProperties']
        }],
        SET_FILL_OPTIONS: [{
          target: 'refreshingEmptyProperties',
          actions: ['setFillOptions'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setFillOptions'],
          cond: 'isIdSelectionMethodWithSelectedProperties'
        }, {
          target: 'idle',
          actions: ['setFillOptions']
        }]
      },
      tags: ['refreshing']
    },
    refreshingEmptyPropertiesByIds: {
      invoke: {
        src: 'getEmptyPropertiesCountByIds',
        onDone: {
          target: 'idle',
          actions: ['setEmptyPropertiesCount']
        },
        onError: {
          target: 'idle',
          actions: ['logError']
        }
      },
      on: {
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        SELECT_PROPERTIES: [{
          target: 'refreshingEmptyProperties',
          actions: ['setSelectedProperties'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setSelectedProperties'],
          cond: 'isIdSelectionMethodWithProperties'
        }, {
          target: 'idle',
          actions: ['setSelectedProperties']
        }],
        SET_FILL_OPTIONS: [{
          target: 'refreshingEmptyProperties',
          actions: ['setFillOptions'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setFillOptions'],
          cond: 'isIdSelectionMethodWithSelectedProperties'
        }, {
          target: 'idle',
          actions: ['setFillOptions']
        }]
      },
      tags: ['refreshing']
    },
    idle: {
      on: {
        SET_FILL_OPTIONS: [{
          target: 'refreshingEmptyProperties',
          actions: ['setFillOptions'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setFillOptions'],
          cond: 'isIdSelectionMethodWithSelectedProperties'
        }, {
          target: 'idle',
          actions: ['setFillOptions']
        }],
        SELECT_PROPERTIES: [{
          target: 'refreshingEmptyProperties',
          actions: ['setSelectedProperties'],
          cond: 'isQuerySelectionMethod'
        }, {
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['setSelectedProperties'],
          cond: 'isIdSelectionMethodWithProperties'
        }, {
          target: 'idle',
          actions: ['setSelectedProperties']
        }],
        SELECTION_CHANGE: [{
          target: 'refreshingEmptyPropertiesByIds',
          actions: ['updateSelection'],
          cond: 'isChangeSelectionToIds'
        }, {
          target: 'refreshingEmptyProperties',
          actions: ['updateSelection']
        }],
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      }
    },
    enriching: {
      invoke: {
        src: 'runSmartFill',
        onDone: {
          target: 'enrichedProperties',
          actions: ['startAIProcessing', 'addEnrichSuccess']
        },
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      }
    },
    enrichedProperties: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'idle',
          actions: ['trackEnrichedRecord', 'closeDrawerAfterEnriched']
        }
      }
    },
    error: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        RETRY: 'loadingUpdates'
      },
      tags: ['error']
    },
    errorEnrichment: {
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      }
    }
  },
  actions: {
    addEnrichError: context => {
      context.onError(context.errorId);
    },
    addEnrichSuccess: context => {
      context.onSuccess(context.selectedProperties.length);
    },
    closeDrawer: context => {
      var _context$onClose;
      (_context$onClose = context.onClose) === null || _context$onClose === void 0 || _context$onClose.call(context, false);
    },
    closeDrawerAfterEnriched: context => {
      var _context$onClose2;
      (_context$onClose2 = context.onClose) === null || _context$onClose2 === void 0 || _context$onClose2.call(context, true);
    },
    logError: (_context, event) => {
      const {
        responseJSON
      } = event.data;
      Raven.captureException(responseJSON);
    },
    setEnrichmentType: assign(context => {
      return {
        enrichmentType: getEnrichmentTypeFromObjectTypeId(context.objectTypeId)
      };
    }),
    setErrorId: assign((_context, event) => {
      const {
        responseJSON
      } = event.data;
      if (responseJSON !== undefined) {
        const {
          correlationId
        } = responseJSON;
        return {
          errorId: correlationId
        };
      }
      return {
        errorId: I18n.text('enrichmentProperties.panel.error.generic.description')
      };
    }),
    setEmptyProperties: assign(() => {
      return {
        properties: []
      };
    }),
    setFillOptions: assign((_context, event) => {
      return {
        fillOption: event.data.fillOption
      };
    }),
    setSmartProperties: assign((_context, event) => {
      return {
        properties: event.data
      };
    }),
    setSelectedProperties: assign((context, event) => {
      const newSelectedProperties = event.data.propertyNames;
      if (context.selectionMethod === 'BY_IDS' && newSelectedProperties.length === 0) {
        return {
          selectedProperties: newSelectedProperties,
          totalWithEmptyProperties: context.selectionIds.length
        };
      }
      return {
        selectedProperties: newSelectedProperties
      };
    }),
    setEmptyPropertiesCount: assign((_context, event) => {
      return {
        totalWithEmptyProperties: event.data
      };
    }),
    setEmptyPropertiesCountError: assign(() => {
      return {
        totalWithEmptyProperties: 0
      };
    }),
    startAIProcessing: context => {
      const {
        selectedProperties,
        objectTypeId,
        selectionIds
      } = context;
      const selectedObjectTypeId = objectTypeId;
      if (context.onEnrich) {
        selectedProperties.forEach(propertyName => {
          var _context$onEnrich;
          (_context$onEnrich = context.onEnrich) === null || _context$onEnrich === void 0 || _context$onEnrich.call(context, selectedObjectTypeId, propertyName, selectionIds);
        });
      }
    },
    trackEnrichedRecord: context => {
      usageTracker.track('enriched-record', {
        objectType: getObjectType(context.objectTypeId),
        enrichmentType: 'smartPropertySingle',
        selectionType: 'manual',
        propertyCount: 0,
        selectionCount: 0,
        selectionDifference: 0
      });
    },
    trackOpenPanel: context => {
      usageTracker.track('interaction', {
        screen: 'smart-properties-panel',
        subscreen: getObjectType(context.objectTypeId),
        action: 'opened-smart-properties-panel'
      });
    },
    updateSelection: assign((_context, event) => {
      return {
        selectionMethod: event.data.selectionMethod,
        selectionIds: event.data.selectionIds,
        selectionCrmSearchQuery: event.data.selectionCrmSearchQuery
      };
    })
  },
  guards: {
    hasPropertyUpdates: (_context, event) => {
      return Object.keys(event.data).length > 0;
    },
    isChangeSelectionToIds: (_context, event) => {
      return event.data.selectionMethod === 'BY_IDS';
    },
    isQuerySelectionMethod: context => {
      return context.selectionMethod === 'BY_QUERY';
    },
    isIdSelectionMethodWithProperties: (context, event) => {
      var _event$data;
      return context.selectionMethod === 'BY_IDS' && event.type === 'SELECT_PROPERTIES' && ((_event$data = event.data) === null || _event$data === void 0 || (_event$data = _event$data.propertyNames) === null || _event$data === void 0 ? void 0 : _event$data.length) > 0;
    },
    isIdSelectionMethodWithSelectedProperties: (context, __event) => {
      return context.selectionMethod === 'BY_IDS' && context.selectedProperties.length > 0;
    }
  },
  services: {
    fetchSmartProperties,
    runSmartFill,
    getEmptyPropertiesCount,
    getEmptyPropertiesCountByIds,
    wait: () => wait()
  }
};